import { CaseStyle, convertCase, makeClassName } from "@hex-insights/core";
import {
	RadioButtonsInput,
	RadioField,
	RadioFieldInputProps,
	RadioOption,
	RadioOptionProps,
} from "@hex-insights/forms";
import { CalendarEventResponseResponse } from "../../../Utilities";
import { CalendarEventResponseFormValues } from "../../FormUtilities";
import { CalendarEventSelectField } from "../CalendarEvent";
import { BaseFieldProps } from "../shared";
import { UserSelectField } from "../User";
import styles from "./Field.module.css";

/**
 * Generic props for fields of the CalendarEventResponse model.
 */
type FieldProps<K extends keyof CalendarEventResponseFormValues.Base = keyof CalendarEventResponseFormValues.Base> =
	BaseFieldProps<Pick<CalendarEventResponseFormValues.Base, K>>;

/**
 * Generic props for fields of the CalendarEventResponse model that only appear in the detail form.
 */
type DetailFieldProps<
	K extends keyof CalendarEventResponseFormValues.Detail = keyof CalendarEventResponseFormValues.Detail,
> = BaseFieldProps<Pick<CalendarEventResponseFormValues.Detail, K>>;

/**
 * Field component for the `response` field of the CalendarEventResponse model.
 */
export function Response({ formState, ...props }: FieldProps<"response">) {
	return (
		<RadioField
			formState={formState}
			name="response"
			options={CalendarEventResponseFormValues.responseOptions}
			blankValue={null}
			Input={ResponseInput}
			{...props}
		/>
	);
}

// Custom

function ResponseInput(props: RadioFieldInputProps<CalendarEventResponseResponse | null>) {
	return (
		<RadioButtonsInput
			{...props}
			Option={ResponseOption}
			optionClassName={styles["response-input__option"]}
			selectedOptionClassName={styles["response-input__option--selected"]}
		/>
	);
}

function ResponseOption({ className, ...props }: RadioOptionProps<CalendarEventResponseResponse | null>) {
	return (
		<RadioOption
			{...props}
			className={makeClassName(
				styles[`response-input__option--${convertCase(props.value ?? "", CaseStyle.Kebab)}`],
				className,
			)}
		/>
	);
}

export namespace Author {
	export type SelectProps = DetailFieldProps<"authorID"> &
		Omit<UserSelectField.SelectProps<CalendarEventResponseFormValues.Detail>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `author` edge of the CalendarEventResponse model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <UserSelectField.Select formState={formState} name="authorID" optional {...props} />;
	}
}

export namespace CalendarEvent {
	export type SelectProps = FieldProps<"calendarEventID"> &
		Omit<CalendarEventSelectField.SelectProps<CalendarEventResponseFormValues.Base>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `calendarEvent` edge of the CalendarEventResponse model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <CalendarEventSelectField.Select formState={formState} name="calendarEventID" {...props} />;
	}
}
